import Dexie, { type Table } from 'dexie';

import {
    getVerifiedLocalTenantId,
    isCanonicalPostgresUuidText,
    LEGACY_LOCAL_DATABASE_NAME,
} from './local-tenant-database';

const CANONICAL_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const TENANT_DATABASE_PREFIX = 'quotemaker_v2::tenant::';
const RECOVERY_REQUIRED_CODE = 'LEGACY_LOCAL_QUEUE_REVIEW_REQUIRED';
const RECOVERY_MESSAGE = 'Recovered from the pre-tenant local cache. Review this item before retrying it.';
const UPGRADE_RECEIPT_VERSION = 1;

type LooseRecord = Record<string, any>;

export type OwnedProjectIdentity = {
    id: unknown;
    dexieId?: unknown;
    dexie_id?: unknown;
};

export type VerifiedLegacyLocalUpgradeResult = {
    status: 'no-legacy-database' | 'no-legacy-project-store' | 'no-owned-project-match' | 'migrated';
    migratedProjects: number;
    skippedUnownedProjects: number;
    skippedAmbiguousProjects: number;
    skippedExistingProjects: number;
    migratedPhotoBlobs: number;
    migratedVideoBlobs: number;
    migratedDocumentBlobs: number;
    recoveredUploadQueueRows: number;
    recoveredBatchJobs: number;
    skippedUnscopedFolderState: boolean;
};

export type UpgradeVerifiedLegacyLocalDataOptions = {
    targetDb: Dexie;
    verifiedTenantId: unknown;
    ownershipTenantId: unknown;
    ownedProjects: OwnedProjectIdentity[];
    legacyDatabaseName?: string;
    now?: () => Date;
};

type NormalizedIdentity = {
    id: string;
    dexieId: string | null;
    sourceKeys: string[];
};

function codedError(code: string, message: string): Error & { code: string } {
    const error = new Error(message) as Error & { code: string };
    error.code = code;
    return error;
}

function tableIfPresent(database: Dexie, name: string): Table<any, any> | null {
    return database.tables.find(table => table.name === name) || null;
}

function normalizedKey(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const key = value.trim();
    return key && key.length <= 512 ? key : null;
}

function normalizedOwnedIdentities(rows: OwnedProjectIdentity[]): NormalizedIdentity[] {
    const byCanonicalId = new Map<string, NormalizedIdentity>();
    for (const row of Array.isArray(rows) ? rows : []) {
        const id = normalizedKey(row?.id);
        if (!id || !CANONICAL_UUID.test(id)) continue;
        const dexieId = normalizedKey(row?.dexieId ?? row?.dexie_id);
        const existing = byCanonicalId.get(id);
        if (existing) {
            if (!existing.dexieId && dexieId && dexieId !== id) existing.dexieId = dexieId;
            continue;
        }
        byCanonicalId.set(id, {
            id,
            dexieId: dexieId && dexieId !== id ? dexieId : null,
            sourceKeys: [],
        });
    }

    const keyOwners = new Map<string, Set<string>>();
    for (const identity of byCanonicalId.values()) {
        for (const key of [identity.id, identity.dexieId].filter(Boolean) as string[]) {
            const owners = keyOwners.get(key) || new Set<string>();
            owners.add(identity.id);
            keyOwners.set(key, owners);
        }
    }
    for (const identity of byCanonicalId.values()) {
        identity.sourceKeys = [identity.id, identity.dexieId]
            .filter((key): key is string => !!key && keyOwners.get(key)?.size === 1);
    }
    return [...byCanonicalId.values()];
}

function uniqueRowsByPrimaryKey(rows: LooseRecord[]): LooseRecord[] {
    const seen = new Set<string>();
    const unique: LooseRecord[] = [];
    for (const row of rows) {
        const key = row?.id == null ? JSON.stringify(row) : String(row.id);
        if (seen.has(key)) continue;
        seen.add(key);
        unique.push(row);
    }
    return unique;
}

async function rowsForProjectKeys(table: Table<any, any> | null, projectKeys: string[]): Promise<LooseRecord[]> {
    if (!table || projectKeys.length === 0) return [];
    const rows: LooseRecord[] = [];
    for (const key of projectKeys) {
        rows.push(...await table.where('projectId').equals(key).toArray());
    }
    return uniqueRowsByPrimaryKey(rows);
}

function withoutAutoId(row: LooseRecord, projectId: string): LooseRecord {
    const { id: _legacyId, ...rest } = row;
    return { ...rest, projectId };
}

function remapMediaReferences(
    value: any,
    photoIds: Map<string, number>,
    videoIds: Map<string, number>,
    documentIds: Map<string, number>,
): any {
    if (Array.isArray(value)) {
        return value.map(entry => remapMediaReferences(entry, photoIds, videoIds, documentIds));
    }
    if (!value || typeof value !== 'object') return value;
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) return value;

    const next: LooseRecord = {};
    for (const [key, child] of Object.entries(value)) {
        const idMap = key === 'blobId' || key === 'photoBlobId'
            ? photoIds
            : key === 'videoBlobId'
                ? videoIds
                : key === 'docBlobId'
                    ? documentIds
                    : null;
        if (idMap && child != null) {
            const mapped = idMap.get(String(child));
            if (mapped !== undefined) next[key] = mapped;
            continue;
        }
        next[key] = remapMediaReferences(child, photoIds, videoIds, documentIds);
    }
    if (!Object.prototype.hasOwnProperty.call(next, 'blobId')) delete next._localBlobOnly;
    return next;
}

function metadataId(value: unknown): string | null {
    if (typeof value === 'string') {
        const id = value.trim();
        return id ? id : null;
    }
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return null;
}

function mergeOwnedMediaLocators(
    currentRows: unknown,
    recoveredRows: unknown,
    locatorKeys: string[],
): unknown {
    if (!Array.isArray(currentRows) || !Array.isArray(recoveredRows)) return currentRows;
    const recoveredById = new Map<string, LooseRecord>();
    for (const row of recoveredRows) {
        const id = metadataId(row?.id);
        if (id) recoveredById.set(id, row);
    }
    return currentRows.map(row => {
        if (!row || typeof row !== 'object') return row;
        const id = metadataId(row.id);
        const recovered = id ? recoveredById.get(id) : null;
        if (!recovered) return row;
        const locators: LooseRecord = {};
        for (const key of locatorKeys) {
            if (row[key] != null) continue;
            const locator = recovered[key];
            if (typeof locator === 'number' && Number.isFinite(locator)) locators[key] = locator;
        }
        return Object.keys(locators).length > 0 ? { ...row, ...locators } : row;
    });
}

function mergeRecoveredMediaLocators(currentProject: LooseRecord, recoveredProject: LooseRecord): LooseRecord {
    const merged = { ...currentProject };
    if (Object.prototype.hasOwnProperty.call(currentProject, 'photos')) {
        merged.photos = mergeOwnedMediaLocators(
            currentProject.photos,
            recoveredProject.photos,
            ['blobId', 'photoBlobId'],
        );
    }
    if (Object.prototype.hasOwnProperty.call(currentProject, 'videos')) {
        merged.videos = mergeOwnedMediaLocators(
            currentProject.videos,
            recoveredProject.videos,
            ['videoBlobId'],
        );
    }
    if (Object.prototype.hasOwnProperty.call(currentProject, 'documents')) {
        merged.documents = mergeOwnedMediaLocators(
            currentProject.documents,
            recoveredProject.documents,
            ['docBlobId'],
        );
    }
    return merged;
}

function projectReceiptKey(tenantId: string, projectId: string): string {
    return `legacyLocalUpgrade:v${UPGRADE_RECEIPT_VERSION}:${tenantId}:project:${projectId}`;
}

function remappedProjectAlias(value: unknown, aliases: Map<string, string>): unknown {
    const key = normalizedKey(value);
    return key && aliases.has(key) ? aliases.get(key)! : value;
}

function remapExplicitProjectRelationships(row: LooseRecord, aliases: Map<string, string>): LooseRecord {
    const next = { ...row };
    for (const key of ['restartOfProjectId', 'restart_of_project_id']) {
        if (Object.prototype.hasOwnProperty.call(next, key)) {
            next[key] = remappedProjectAlias(next[key], aliases);
        }
    }
    for (const key of ['projectFolder', 'project_folder']) {
        const folder = next[key];
        if (!folder || typeof folder !== 'object' || Array.isArray(folder)) continue;
        const remappedFolder = { ...folder };
        for (const relationKey of ['restartOfProjectId', 'restart_of_project_id']) {
            if (Object.prototype.hasOwnProperty.call(remappedFolder, relationKey)) {
                remappedFolder[relationKey] = remappedProjectAlias(remappedFolder[relationKey], aliases);
            }
        }
        next[key] = remappedFolder;
    }
    return next;
}

function remapExplicitBatchRelationships(row: LooseRecord, aliases: Map<string, string>): LooseRecord {
    const next = remapExplicitProjectRelationships(row, aliases);
    for (const key of ['reviewDetails', 'review_details']) {
        const review = next[key];
        if (!review || typeof review !== 'object' || Array.isArray(review)) continue;
        const remappedReview = { ...review };
        for (const relationKey of ['replacementProjectId', 'replacement_project_id']) {
            if (Object.prototype.hasOwnProperty.call(remappedReview, relationKey)) {
                remappedReview[relationKey] = remappedProjectAlias(remappedReview[relationKey], aliases);
            }
        }
        next[key] = remappedReview;
    }
    return next;
}

function recoveredUploadQueueRow(row: LooseRecord, projectId: string, photoIds: Map<string, number>, recoveredAt: string) {
    const { id: _legacyId, ...rest } = row;
    const oldStatus = normalizedKey(row?.status) || 'unknown';
    const mappedBlobId = row?.blobId == null ? undefined : photoIds.get(String(row.blobId));
    const safeTerminal = oldStatus === 'confirmed' || oldStatus === 'failed';
    const recovered: LooseRecord = {
        ...rest,
        projectId,
        status: safeTerminal ? oldStatus : 'failed',
        recoveredFromLegacyStatus: oldStatus,
        recoveredFromLegacyAt: recoveredAt,
        claimToken: null,
        claimOwner: null,
        claimOwnerId: null,
        leaseHeartbeatAt: null,
        leaseExpiresAt: null,
        nextAttemptAt: null,
        ...(mappedBlobId === undefined ? {} : { blobId: mappedBlobId }),
    };
    if (mappedBlobId === undefined) delete recovered.blobId;
    if (!safeTerminal) {
        recovered.legacyError = row?.error ?? null;
        recovered.error = RECOVERY_MESSAGE;
        recovered.errorCode = RECOVERY_REQUIRED_CODE;
    }
    return recovered;
}

function recoveredBatchJob(row: LooseRecord, projectId: string, recoveredAt: string) {
    const { id: _legacyId, ...rest } = row;
    const oldStatus = normalizedKey(row?.status) || 'unknown';
    const safeTerminal = ['completed', 'failed', 'skipped', 'cancelled'].includes(oldStatus);
    return {
        ...rest,
        projectId,
        status: safeTerminal ? oldStatus : 'failed',
        recoveredFromLegacyStatus: oldStatus,
        recoveredFromLegacyAt: recoveredAt,
        claimOwnerId: null,
        leaseHeartbeatAt: null,
        leaseExpiresAt: null,
        ...(!safeTerminal ? {
            legacyLastError: row?.lastError ?? null,
            lastError: RECOVERY_MESSAGE,
            lastErrorCode: RECOVERY_REQUIRED_CODE,
        } : {}),
    };
}

function emptyResult(status: VerifiedLegacyLocalUpgradeResult['status']): VerifiedLegacyLocalUpgradeResult {
    return {
        status,
        migratedProjects: 0,
        skippedUnownedProjects: 0,
        skippedAmbiguousProjects: 0,
        skippedExistingProjects: 0,
        migratedPhotoBlobs: 0,
        migratedVideoBlobs: 0,
        migratedDocumentBlobs: 0,
        recoveredUploadQueueRows: 0,
        recoveredBatchJobs: 0,
        skippedUnscopedFolderState: false,
    };
}

async function legacyHasUnscopedFolderState(legacyDb: Dexie): Promise<boolean> {
    const appSettings = tableIfPresent(legacyDb, 'app_settings');
    const roots = tableIfPresent(legacyDb, 'fe_root');
    const [handle, rootCount] = await Promise.all([
        appSettings?.get('syncFolderHandle').catch(() => null) || null,
        roots?.count().catch(() => 0) || 0,
    ]);
    return !!handle || Number(rootCount) > 0;
}

type LegacyProjectSnapshot = {
    identity: NormalizedIdentity;
    sourceProject: LooseRecord;
    photos: LooseRecord[];
    videos: LooseRecord[];
    documents: LooseRecord[];
    uploads: LooseRecord[];
    jobs: LooseRecord[];
};

async function readLegacySnapshot(
    legacyDb: Dexie,
    sourceProjects: Table<any, any>,
    identities: NormalizedIdentity[],
): Promise<{
    plans: LegacyProjectSnapshot[];
    skippedUnownedProjects: number;
    skippedAmbiguousProjects: number;
    skippedUnscopedFolderState: boolean;
}> {
    const sourceTables = {
        photo_blobs: tableIfPresent(legacyDb, 'photo_blobs'),
        video_blobs: tableIfPresent(legacyDb, 'video_blobs'),
        document_blobs: tableIfPresent(legacyDb, 'document_blobs'),
        upload_queue: tableIfPresent(legacyDb, 'upload_queue'),
        batch_jobs: tableIfPresent(legacyDb, 'batch_jobs'),
    };
    const transactionTables = [
        sourceProjects,
        ...Object.values(sourceTables),
        tableIfPresent(legacyDb, 'app_settings'),
        tableIfPresent(legacyDb, 'fe_root'),
    ].filter((table): table is Table<any, any> => !!table);

    return (legacyDb as any).transaction('r', ...transactionTables, async () => {
        const totalLegacyProjects = await sourceProjects.count();
        const candidateSourceIds = new Set<string>();
        const selectedPlans: Array<{ identity: NormalizedIdentity; sourceProject: LooseRecord }> = [];
        let skippedAmbiguousProjects = 0;

        for (const identity of identities) {
            const candidates = uniqueRowsByPrimaryKey((await sourceProjects.bulkGet(identity.sourceKeys))
                .filter((row): row is LooseRecord => !!row));
            for (const candidate of candidates) candidateSourceIds.add(String(candidate.id));
            if (candidates.length > 1) {
                skippedAmbiguousProjects += candidates.length;
                continue;
            }
            if (candidates.length === 1) selectedPlans.push({ identity, sourceProject: candidates[0]! });
        }

        const plans: LegacyProjectSnapshot[] = [];
        for (const plan of selectedPlans) {
            const [photos, videos, documents, uploads, jobs] = await Promise.all([
                rowsForProjectKeys(sourceTables.photo_blobs, plan.identity.sourceKeys),
                rowsForProjectKeys(sourceTables.video_blobs, plan.identity.sourceKeys),
                rowsForProjectKeys(sourceTables.document_blobs, plan.identity.sourceKeys),
                rowsForProjectKeys(sourceTables.upload_queue, plan.identity.sourceKeys),
                rowsForProjectKeys(sourceTables.batch_jobs, plan.identity.sourceKeys),
            ]);
            plans.push({ ...plan, photos, videos, documents, uploads, jobs });
        }

        return {
            plans,
            skippedUnownedProjects: Math.max(0, totalLegacyProjects - candidateSourceIds.size),
            skippedAmbiguousProjects,
            skippedUnscopedFolderState: await legacyHasUnscopedFolderState(legacyDb),
        };
    });
}

/**
 * Copy only project-scoped legacy cache rows whose identities were returned by
 * the authenticated tenant's project API. The source database is read-only and
 * every target project is committed atomically. Unscoped settings, local-only
 * projects and folder handles remain in place for explicit operator recovery.
 * This bounded bridge does not inspect the older raw `quotemaker_db` database
 * or copy unrelated legacy stores. Expanding that recovery surface requires a
 * separate ownership contract rather than an inferred tenant assignment.
 */
export async function upgradeVerifiedLegacyLocalData({
    targetDb,
    verifiedTenantId,
    ownershipTenantId,
    ownedProjects,
    legacyDatabaseName = LEGACY_LOCAL_DATABASE_NAME,
    now = () => new Date(),
}: UpgradeVerifiedLegacyLocalDataOptions): Promise<VerifiedLegacyLocalUpgradeResult> {
    const tenantId = normalizedKey(verifiedTenantId);
    if (!tenantId || !isCanonicalPostgresUuidText(tenantId) || getVerifiedLocalTenantId() !== tenantId) {
        throw codedError(
            'LOCAL_TENANT_NOT_VERIFIED',
            'Legacy local data upgrade requires the exact server-verified tenant binding.',
        );
    }
    if (normalizedKey(ownershipTenantId) !== tenantId) {
        throw codedError(
            'LOCAL_TENANT_OWNERSHIP_RESPONSE_MISMATCH',
            'The authenticated project-ownership response does not match the verified local tenant.',
        );
    }
    if (!targetDb
        || targetDb.name !== `${TENANT_DATABASE_PREFIX}${tenantId}`
        || targetDb.name === legacyDatabaseName) {
        throw codedError(
            'LOCAL_TENANT_UPGRADE_TARGET_INVALID',
            'Legacy local data upgrade requires the exact verified tenant database.',
        );
    }
    if (!await Dexie.exists(legacyDatabaseName)) return emptyResult('no-legacy-database');

    const legacyDb = new Dexie(legacyDatabaseName);
    try {
        await legacyDb.open();
        const sourceProjects = tableIfPresent(legacyDb, 'projects');
        if (!sourceProjects) return emptyResult('no-legacy-project-store');
        const targetProjects = tableIfPresent(targetDb, 'projects');
        const targetSettings = tableIfPresent(targetDb, 'app_settings');
        if (!targetProjects || !targetSettings) {
            throw codedError(
                'LOCAL_TENANT_UPGRADE_TARGET_SCHEMA_MISSING',
                'The tenant-scoped database is missing required upgrade stores.',
            );
        }

        const result = emptyResult('no-owned-project-match');
        const identities = normalizedOwnedIdentities(ownedProjects);
        const projectAliases = new Map<string, string>();
        for (const identity of identities) {
            for (const sourceKey of identity.sourceKeys) projectAliases.set(sourceKey, identity.id);
        }
        const snapshot = await readLegacySnapshot(legacyDb, sourceProjects, identities);
        result.skippedUnscopedFolderState = snapshot.skippedUnscopedFolderState;
        result.skippedUnownedProjects = snapshot.skippedUnownedProjects;
        result.skippedAmbiguousProjects = snapshot.skippedAmbiguousProjects;
        const targetTables = {
            photo_blobs: tableIfPresent(targetDb, 'photo_blobs'),
            video_blobs: tableIfPresent(targetDb, 'video_blobs'),
            document_blobs: tableIfPresent(targetDb, 'document_blobs'),
            upload_queue: tableIfPresent(targetDb, 'upload_queue'),
            batch_jobs: tableIfPresent(targetDb, 'batch_jobs'),
        };
        const recoveredAt = now().toISOString();

        for (const plan of snapshot.plans) {
            const { photos, videos, documents, uploads, jobs } = plan;
            for (const [storeName, rows] of Object.entries({
                photo_blobs: photos,
                video_blobs: videos,
                document_blobs: documents,
                upload_queue: uploads,
                batch_jobs: jobs,
            })) {
                if (rows.length > 0 && !targetTables[storeName as keyof typeof targetTables]) {
                    throw codedError(
                        'LOCAL_TENANT_UPGRADE_TARGET_SCHEMA_MISSING',
                        `The tenant-scoped database is missing the ${storeName} recovery store.`,
                    );
                }
            }

            const transactionTables = [
                targetProjects,
                targetSettings,
                ...Object.values(targetTables).filter((table): table is Table<any, any> => !!table),
            ];
            const migrated = await (targetDb as any).transaction('rw', ...transactionTables, async () => {
                const receiptKey = projectReceiptKey(tenantId, plan.identity.id);
                if (await targetSettings.get(receiptKey)) return null;
                const targetAliasKeys = plan.identity.sourceKeys
                    .filter(sourceKey => sourceKey !== plan.identity.id);
                const targetAliasRows = targetAliasKeys.length > 0
                    ? (await targetProjects.bulkGet(targetAliasKeys)).filter(Boolean)
                    : [];
                if (targetAliasRows.length > 0) {
                    throw codedError(
                        'LOCAL_TENANT_UPGRADE_TARGET_PROJECT_AMBIGUOUS',
                        'A legacy Project alias already exists in the tenant database and requires explicit review.',
                    );
                }
                const currentProject = await targetProjects.get(plan.identity.id);
                if (currentProject?.tenantId && normalizedKey(currentProject.tenantId) !== tenantId) {
                    throw codedError(
                        'LOCAL_TENANT_UPGRADE_EXISTING_PROJECT_TENANT_MISMATCH',
                        'The existing local Project does not belong to the verified tenant.',
                    );
                }
                const photoIds = new Map<string, number>();
                const videoIds = new Map<string, number>();
                const documentIds = new Map<string, number>();
                for (const row of photos) {
                    const newId = await targetTables.photo_blobs!.add(withoutAutoId(row, plan.identity.id));
                    photoIds.set(String(row.id), Number(newId));
                }
                for (const row of videos) {
                    const newId = await targetTables.video_blobs!.add(withoutAutoId(row, plan.identity.id));
                    videoIds.set(String(row.id), Number(newId));
                }
                for (const row of documents) {
                    const newId = await targetTables.document_blobs!.add(withoutAutoId(row, plan.identity.id));
                    documentIds.set(String(row.id), Number(newId));
                }

                const project = remapExplicitProjectRelationships(
                    remapMediaReferences(
                        plan.sourceProject,
                        photoIds,
                        videoIds,
                        documentIds,
                    ),
                    projectAliases,
                );
                delete project.tenant_id;
                delete project.dexie_id;
                project.id = plan.identity.id;
                project.tenantId = tenantId;
                if (plan.identity.dexieId) project.dexieId = plan.identity.dexieId;
                else if (String(plan.sourceProject.id) !== plan.identity.id) {
                    project.dexieId = String(plan.sourceProject.id);
                }
                await targetProjects.put(currentProject
                    ? mergeRecoveredMediaLocators(currentProject, project)
                    : project);

                for (const row of uploads) {
                    await targetTables.upload_queue!.add(recoveredUploadQueueRow(
                        row,
                        plan.identity.id,
                        photoIds,
                        recoveredAt,
                    ));
                }
                for (const row of jobs) {
                    await targetTables.batch_jobs!.add(recoveredBatchJob(
                        remapExplicitBatchRelationships(row, projectAliases),
                        plan.identity.id,
                        recoveredAt,
                    ));
                }
                await targetSettings.put({
                    key: receiptKey,
                    value: {
                        version: UPGRADE_RECEIPT_VERSION,
                        sourceDatabase: legacyDatabaseName,
                        sourceProjectId: String(plan.sourceProject.id),
                        projectId: plan.identity.id,
                        recoveredAt,
                    },
                });
                return {
                    photos: photos.length,
                    videos: videos.length,
                    documents: documents.length,
                    uploads: uploads.length,
                    jobs: jobs.length,
                };
            });
            if (!migrated) {
                result.skippedExistingProjects += 1;
                continue;
            }
            result.migratedProjects += 1;
            result.migratedPhotoBlobs += migrated.photos;
            result.migratedVideoBlobs += migrated.videos;
            result.migratedDocumentBlobs += migrated.documents;
            result.recoveredUploadQueueRows += migrated.uploads;
            result.recoveredBatchJobs += migrated.jobs;
        }

        if (result.migratedProjects > 0) result.status = 'migrated';
        await targetSettings.put({
            key: `legacyLocalUpgrade:v${UPGRADE_RECEIPT_VERSION}:${tenantId}`,
            value: {
                ...result,
                version: UPGRADE_RECEIPT_VERSION,
                sourceDatabase: legacyDatabaseName,
                checkedAt: recoveredAt,
            },
        });
        return result;
    } finally {
        legacyDb.close();
    }
}
